import { useMemo } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { Content, useContent } from 'src/contexts/ContentContext';
import { Stack } from 'src/core/ui';
import { ContentId } from 'src/utils/constants/contentId';
import {
  getCurrencyNumberFormat,
  getQuantityNumberFormat,
} from 'src/utils/format';
import { EventWithData, ListingMetrics } from 'src/WebApiController';

import * as styles from './PortfolioInsights.css';
import { InsightMetrics } from './PortfolioInsights.types';

export const usePortfolioInsightMetrics = (
  events: EventWithData[],
  eventMetrics?: Record<string, ListingMetrics>
) => {
  const { loginContext } = useAppContext();
  const eventText = useContent(ContentId.Event);
  const eventsText = useContent(ContentId.Events);

  const currency = useMemo(
    () => loginContext?.user?.activeAccount?.currencyCode ?? undefined,
    [loginContext?.user?.activeAccount?.currencyCode]
  );

  const eventsCountText = useMemo(() => {
    const count = events.length;
    if (count === 1) {
      return `${count} ${eventText}`;
    }
    return `${count} ${eventsText}`;
  }, [eventText, events.length, eventsText]);

  const filteredEventMetrics = useMemo(() => {
    return events
      .map((e) => eventMetrics?.[e.event.virtId])
      .filter((m: ListingMetrics | undefined): m is ListingMetrics => !!m);
  }, [eventMetrics, events]);

  const insightMetrics = useMemo((): InsightMetrics[] => {
    const {
      formattedTotalListed,
      formattedSoldTickets,
      formattedTotalTickets,
      formattedTicketSoldRate,
      formattedTotalSales,
      formattedProfitAndLoss,
      formattedAverageTicketPrice,
    } = getPortfolioInsightFormattedMetrics(filteredEventMetrics, currency);

    const insights: InsightMetrics[] = [];
    insights.push({
      title: ContentId.TotalValueListed,
      mainMetrics: <span>{formattedTotalListed}</span>,
      subMetrics: <span>{eventsCountText}</span>,
    });
    insights.push({
      title: ContentId.SoldTickets,
      mainMetrics: <span>{formattedSoldTickets}</span>,
      mainMetricsBadge: <span>{formattedTicketSoldRate}</span>,
      subMetrics: (
        <Stack gap="s" alignItems="center">
          <span>{formattedTotalTickets}</span>
          <Content id={ContentId.Total} />
        </Stack>
      ),
    });
    insights.push({
      title: ContentId.Sales,
      mainMetrics: <span>{formattedTotalSales}</span>,
      subMetrics: (
        <Stack gap="s" alignItems="center">
          <span className={styles.portfolioInsightSecondaryMetricsPrefix}>
            <Content id={ContentId.PandL} />
          </span>
          {formattedProfitAndLoss}
        </Stack>
      ),
    });
    insights.push({
      title: ContentId.AverageTicketPrice,
      mainMetrics: <span>{formattedAverageTicketPrice}</span>,
      subMetrics: (
        <Stack gap="s" alignItems="center">
          <span>{formattedTotalTickets}</span>
          <Content id={ContentId.Total} />
        </Stack>
      ),
    });
    return insights;
  }, [currency, eventsCountText, filteredEventMetrics]);

  return {
    insightMetrics,
  };
};

const getPortfolioInsightFormattedMetrics = (
  metrics: ListingMetrics[],
  currency?: string
) => {
  const {
    totalListed,
    soldTickets,
    totalTickets,
    totalTicketPrice,
    totalSales,
    profitAndLoss,
  } = metrics.reduce(
    (acc, listingMetrics) => {
      acc.totalListed += listingMetrics.ttlListPrc;
      acc.soldTickets += listingMetrics.soldQty;
      acc.totalTickets += listingMetrics.tktQty;
      acc.totalSales += listingMetrics.soldCst;
      acc.profitAndLoss += listingMetrics.pandL;
      acc.totalTicketPrice += listingMetrics.ttlListPrc;
      return acc;
    },
    {
      totalListed: 0,
      soldTickets: 0,
      totalTickets: 0,
      totalTicketPrice: 0,
      totalSales: 0,
      profitAndLoss: 0,
    }
  );

  const soldRate = totalTickets === 0 ? 0 : soldTickets / totalTickets;
  const averageTicketPrice =
    totalTicketPrice === 0 ? 0 : totalTicketPrice / totalTickets;
  return {
    formattedTotalListed: getCurrencyNumberFormat(totalListed, currency).format(
      totalListed
    ),
    formattedSoldTickets: getQuantityNumberFormat().format(soldTickets),
    formattedTotalTickets: getQuantityNumberFormat().format(totalTickets),
    formattedTicketSoldRate: soldRate.toLocaleString(undefined, {
      style: 'percent',
      minimumFractionDigits: 0,
    }),
    formattedAverageTicketPrice: getCurrencyNumberFormat(
      averageTicketPrice,
      currency
    ).format(averageTicketPrice),
    formattedTotalSales: getCurrencyNumberFormat(totalSales, currency).format(
      totalSales
    ),
    formattedProfitAndLoss: getCurrencyNumberFormat(
      profitAndLoss,
      currency
    ).format(profitAndLoss),
  };
};
